if (!customElements.get('infinite-scroll-products')) {
  class InfiniteScrollProducts extends HTMLElement {
    constructor() {
      super();
      this.loadNextPage = this.loadNextPage.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('.infinite-scroll-products__button');
      this.buttonLabel = this.button?.querySelector('span');
      this.status = this.querySelector('.infinite-scroll-products__status');
      this.grid = this.findGrid();
      this.observationTarget = this.findObservationTarget();
      this.abortController = new AbortController();
      this.isLoading = false;
      if (!this.button || !this.buttonLabel || !this.status || !this.grid) return;
      this.setAttribute('data-enhanced', 'true');
      this.button.addEventListener('click', this.loadNextPage);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersection, { rootMargin: '600px 0px' });
        this.setAttribute('data-auto', 'true');
      }
      this.initialize();
    }

    findGrid() {
      return this.closest('#ProductGridContainer')?.querySelector('#product-grid');
    }

    findObservationTarget() {
      return this;
    }

    initialize() {
      if (this.hasMore()) this.observer?.observe(this.observationTarget);
      else this.complete();
    }

    hasMore() {
      return Boolean(this.dataset.nextUrl);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
      this.button?.removeEventListener('click', this.loadNextPage);
    }

    handleIntersection(entries) {
      if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
    }

    async readPage(nextUrl, sectionId) {
      const url = new URL(nextUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid collection URL');
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Request failed');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    appendProduct(product) {
      product.classList.add('scroll-trigger--cancel');
      product.style.removeProperty('--animation-order');
      product.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.setAttribute('fetchpriority', 'auto');
      });
      this.grid.append(product);
    }

    async runLoad(load) {
      if (this.isLoading || !this.isConnected || !this.hasMore()) return;
      this.isLoading = true;
      this.observer?.unobserve(this.observationTarget);
      this.removeAttribute('data-error');
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.buttonLabel.textContent = this.dataset.loadingLabel;
      this.status.textContent = this.dataset.loadingLabel;
      try {
        await load();
        if (!this.isConnected || this.abortController.signal.aborted) return;
        if (!this.hasMore()) this.complete();
        else {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
          this.buttonLabel.textContent = this.dataset.loadLabel;
          this.status.textContent = this.dataset.loadedLabel;
          if (!this.hidden) this.observer?.observe(this.observationTarget);
        }
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected) return;
        this.setAttribute('data-error', 'true');
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
        this.buttonLabel.textContent = this.dataset.retryLabel;
        this.status.textContent = this.dataset.errorLabel;
      } finally {
        this.isLoading = false;
      }
    }

    loadNextPage() {
      return this.runLoad(async () => {
        const html = await this.readPage(this.dataset.nextUrl, this.dataset.sectionId);
        const sourceContainer = html.getElementById('ProductGridContainer');
        const sourceGrid = sourceContainer?.querySelector('#product-grid');
        if (!sourceGrid) throw new Error('Product grid missing');
        if (!this.isConnected || this.abortController.signal.aborted) return;
        Array.from(sourceGrid.children).forEach((product) => this.appendProduct(product));
        this.dataset.nextUrl = sourceContainer.querySelector('infinite-scroll-products')?.dataset.nextUrl || '';
      });
    }

    complete(emitExhaustion = true) {
      this.observer?.disconnect();
      this.dataset.nextUrl = '';
      this.setAttribute('data-complete', 'true');
      this.button.hidden = true;
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
      this.status.textContent = this.dataset.endLabel;
      if (emitExhaustion && !this.hasAttribute('data-exhausted-emitted')) {
        this.setAttribute('data-exhausted-emitted', 'true');
        this.dispatchEvent(new CustomEvent('mm:collection-exhausted', {
          bubbles: true, detail: { collectionHandle: this.dataset.collectionHandle || '' },
        }));
      }
    }
  }
  customElements.define('infinite-scroll-products', InfiniteScrollProducts);
}

if (!customElements.get('mm-discovery-feed')) {
  class MMDiscoveryFeed extends customElements.get('infinite-scroll-products') {
    findGrid() {
      return this.querySelector('.product-grid');
    }

    findObservationTarget() {
      return this.querySelector('.infinite-scroll-products__controls');
    }

    initialize() {
      this.queue = [];
      this.visited = new Set();
      this.seen = new Set();
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.start = this.start.bind(this);
      this.container = this.closest('#ProductGridContainer');
      this.container?.addEventListener('mm:collection-exhausted', this.start);
      this.mobile.addEventListener('change', this.start);
      // Cached facet renders connect before history.pushState completes.
      queueMicrotask(this.start);
    }

    start() {
      if (!this.isConnected) return;
      const category = this.container?.querySelector('infinite-scroll-products');
      const waiting = category && category.hasMore();
      this.hidden = !this.mobile.matches || waiting || !this.validContext() ||
        (!this.hasMore() && !this.grid.children.length);
      if (this.hidden) this.observer?.unobserve(this.observationTarget);
      else if (!this.hasAttribute('data-complete')) this.observer?.observe(this.observationTarget);
    }

    validContext() {
      if (!this.dataset.collectionHandle) return true;
      const params = new URLSearchParams(window.location.search);
      return !Array.from(params).some(([key, value]) => value &&
        (key.startsWith('filter.') || (key === 'sort_by' && value !== this.dataset.defaultSort))) &&
        !this.container?.querySelector('.collection.loading');
    }

    hasMore() {
      return Boolean(this.queue?.length || this.dataset.nextUrl);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.container?.removeEventListener('mm:collection-exhausted', this.start);
      this.mobile?.removeEventListener('change', this.start);
    }

    eligible(product) {
      const id = product.dataset.mmProductId;
      return id && !this.seen.has(id) && (!this.dataset.collectionId ||
        !(product.dataset.collectionIds || '').split(',').includes(this.dataset.collectionId));
    }

    sourceURL(nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      const root = this.dataset.rootUrl || '/';
      if (root !== '/' && !url.pathname.startsWith(root)) {
        url.pathname = root.replace(/\/$/, '') + url.pathname;
      }
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.delete('section_id');
      return url;
    }

    loadNextPage() {
      this.start();
      if (this.hidden) return;
      return this.runLoad(async () => {
        document.querySelectorAll('[data-mm-product-id]').forEach((card) => {
          if (card.dataset.mmProductId) this.seen.add(card.dataset.mmProductId);
        });
        let added = 0;
        let requests = 0;
        // ponytail: sparse pages scan at most eight per batch; later batches continue the cursor without a total cap.
        while (added < 8 && this.hasMore() && requests < 8) {
          if (!this.isConnected || !this.validContext() || !this.mobile.matches) {
            this.start();
            return;
          }
          if (!this.queue.length && this.dataset.nextUrl) {
            const url = this.sourceURL(this.dataset.nextUrl);
            const html = await this.readPage(url.href, this.dataset.sectionId);
            if (!this.isConnected || this.abortController.signal.aborted || !this.validContext() || !this.mobile.matches) {
              this.start();
              return;
            }
            const page = html.querySelector('template[data-mm-discovery-cards]');
            const list = page?.content.querySelector('ul');
            if (!list) throw new Error('Discovery cards missing');
            this.visited.add(url.href);
            const next = page.dataset.nextUrl ? this.sourceURL(page.dataset.nextUrl).href : '';
            this.dataset.nextUrl = this.visited.has(next) ? '' : next;
            this.queue = Array.from(list.children).filter((product) => this.eligible(product));
            if (this.dataset.shuffleBatch !== 'false') {
              for (let i = this.queue.length - 1; i > 0; i -= 1) {
                const j = Math.floor(Math.random() * (i + 1));
                [this.queue[i], this.queue[j]] = [this.queue[j], this.queue[i]];
              }
            }
            requests += 1;
          }
          while (this.queue.length && added < 16) {
            const product = this.queue.shift();
            if (!this.eligible(product)) continue;
            this.seen.add(product.dataset.mmProductId);
            this.appendProduct(product);
            added += 1;
          }
        }
      });
    }

    complete() {
      super.complete(false);
      if (!this.grid.children.length) this.hidden = true;
    }
  }
  customElements.define('mm-discovery-feed', MMDiscoveryFeed);
}
